import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, ChevronRight, ShieldCheck, Info, Check, ArrowLeft, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SelloBadge from "@/components/SelloBadge";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { useFavorites } from "@/context/FavoritesContext";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        api.get("/products", { params: { category: data.category } }).then(({ data: rel }) => {
          setRelated(rel.filter((p) => p.id !== data.id).slice(0, 4));
        });
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const handleBuy = () => {
    api.post("/track-click", { product_id: product.id }).catch(() => {});
    toast.success("Abriendo tienda oficial en una nueva pestaña…");
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-32 text-center">
          <h1 className="font-display text-3xl font-bold">Producto no encontrado</h1>
          <Button onClick={() => navigate("/catalogo")} className="mt-6">Volver al catálogo</Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-32 text-center font-mono-tech text-sm text-emerald-400 animate-pulse">Cargando producto…</div>
      </div>
    );
  }

  const affiliatePath = `/recomienda/${product.category}/${product.slug}`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-8">
        <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:text-emerald-400">Inicio</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/categoria/${product.category}`} className="hover:text-emerald-400 capitalize">{product.category.replace(/-/g, " ")}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-emerald-400 line-clamp-1">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="relative">
            <div className="sticky top-24 overflow-hidden rounded-3xl border border-border bg-secondary aspect-square">
              <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
              <div className="absolute top-4 left-4"><SelloBadge size="lg" /></div>
            </div>
          </motion.div>

          <div>
            <span className="font-mono-tech text-xs uppercase tracking-widest text-emerald-500/80">{product.brand}</span>
            <h1 className="mt-2 font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">{product.name}</h1>

            <div className="mt-4 flex flex-wrap gap-2">
              {product.tags.map((t) => (
                <span key={t} data-testid={`detail-tag-${t}`} className="rounded-full bg-secondary border border-border px-3 py-1 text-xs font-medium text-muted-foreground">{t}</span>
              ))}
            </div>

            {product.price != null && (
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-3xl font-extrabold">${product.price.toFixed(2)}</span>
                <span className="text-sm text-muted-foreground">{product.currency} · precio estimado en {product.retailer}</span>
              </div>
            )}

            <p className="mt-5 text-muted-foreground leading-relaxed">{product.description}</p>

            {/* Sello block */}
            <div data-testid="sello-block" className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
                <h3 className="font-display font-bold text-emerald-400">Por qué obtuvo el Sello ZeroToxic</h3>
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{product.sello}</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {[["0%", "Tóxicos"], [`${product.purity_score}%`, "Pureza"], ["3ª parte", "Verificado"]].map(([n, l]) => (
                  <div key={l} className="rounded-xl bg-background border border-border p-3 text-center">
                    <div className="font-display font-extrabold text-emerald-400">{n}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
            </div>

            <a
              data-testid="detail-buy-btn"
              href={affiliatePath}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBuy}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 px-6 py-4 font-bold text-slate-950 text-base transition-all hover:scale-[1.01]"
            >
              Comprar en Tienda Oficial <ExternalLink className="h-4 w-4" />
            </a>

            <button
              data-testid="detail-fav-btn"
              onClick={() => {
                const added = toggleFavorite(product);
                toast[added ? "success" : "message"](added ? "Añadido a favoritos" : "Quitado de favoritos");
              }}
              className={`mt-3 flex w-full items-center justify-center gap-2 rounded-full border px-6 py-3 font-semibold transition-all ${isFavorite(product.id) ? "border-emerald-500 bg-emerald-500/15 text-emerald-400" : "border-border hover:border-emerald-500/50 text-foreground"}`}
            >
              <Heart className="h-4 w-4" fill={isFavorite(product.id) ? "currentColor" : "none"} />
              {isFavorite(product.id) ? "En tus favoritos" : "Guardar en favoritos"}
            </button>

            {/* FTC notice */}
            <div data-testid="ftc-notice" className="mt-4 flex gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
              <Info className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-semibold text-amber-500">Aviso de afiliado:</span> este es un enlace de afiliado. Si compras a través de él,
                ZeroToxicMarket puede recibir una comisión sin costo adicional para ti.
              </p>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-8">Productos relacionados</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}

        <Link to="/catalogo" className="mt-12 inline-flex items-center gap-1 text-sm text-emerald-400 hover:gap-2 transition-all">
          <ArrowLeft className="h-4 w-4" /> Volver al catálogo
        </Link>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
